
import React, { useCallback, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import SectionHeading from '@/components/ui/SectionHeading';
import { ArrowRight, LogIn, Globe, Shield, Database, Server, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import OnboardingForm from './OnboardingForm';

type Provider = 'google' | 'microsoft';

const SecurePlatformConnection: React.FC = () => {
  const { session } = useAuth();
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);

  // Connection status
  const [googleConnected, setGoogleConnected] = useState(false);
  const [microsoftConnected, setMicrosoftConnected] = useState(false);

  // Track onboarding flow
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Store onboarding submission (just for demo – in real app, persist)
  const [onboarded, setOnboarded] = useState(false);

  // Hard-code the Google client ID for testing
  const GOOGLE_CLIENT_ID = "594289857274-je0g0i1im91gjuauts094ojgogq09eci";
  const GOOGLE_REDIRECT_URI = "https://thinktomorrow.ai/onboarding";
  const MICROSOFT_CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID;

  // On page load, check if redirected back (OAuth success/failure)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Try to detect OAuth status (based on code or errors from Google/Microsoft)
    if (params.has("code")) {
      const state = params.get("state") || "";
      // For demo: check if Google or Microsoft by value of state param (ideally, set custom "provider" value in state param when redirecting)
      if (window.location.href.includes("accounts.google.com") || params.get("provider") === "google" || document.referrer.includes("accounts.google.com")) {
        setGoogleConnected(true);
        toast({ title: "Google Connected!", description: "Successfully connected to Google Workspace." });
      } else {
        setMicrosoftConnected(true);
        toast({ title: "Microsoft Connected!", description: "Successfully connected to Microsoft 365." });
      }
      setShowOnboarding(true);
      // Remove code param from URL to avoid repeat effects on reload
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast]);

  const handleOAuthConnect = useCallback(async (provider: Provider) => {
    setIsConnecting(true);
    try {
      let authUrl = '';
      if (provider === 'google') {
        if (!GOOGLE_CLIENT_ID) throw new Error('Google Client ID not configured.');
        const scopes = [
          'https://www.googleapis.com/auth/userinfo.email',
          'https://www.googleapis.com/auth/userinfo.profile'
        ];
        // Add 'provider' param so we can tell on return which succeeded
        authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}&redirect_uri=${encodeURIComponent(GOOGLE_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(scopes.join(' '))}&access_type=offline&prompt=consent&state=google`;
      } else {
        if (!MICROSOFT_CLIENT_ID) throw new Error('Microsoft Client ID not configured');
        const scopes = encodeURIComponent([
          'offline_access',
          'https://graph.microsoft.com/Mail.Send',
          'https://graph.microsoft.com/Calendars.ReadWrite'
        ].join(' '));
        authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${encodeURIComponent(MICROSOFT_CLIENT_ID)}&response_type=code&redirect_uri=${encodeURIComponent(GOOGLE_REDIRECT_URI)}&scope=${scopes}&state=microsoft`;
      }
      window.location.href = authUrl;
    } catch (error) {
      console.error(`Error initiating ${provider} OAuth flow:`, error);
      toast({
        title: "Connection Error",
        description: error instanceof Error ? error.message : "Failed to initiate connection. Please try again.",
        variant: "destructive"
      });
      setIsConnecting(false);
    }
  }, [toast, GOOGLE_CLIENT_ID, MICROSOFT_CLIENT_ID, GOOGLE_REDIRECT_URI]);

  // Receive info from OnboardingForm
  const handleOnboardingSubmit = (data: { name: string; email: string }) => {
    setOnboarded(true);
    toast({
      title: "Thank you!",
      description: "Onboarding submitted.",
    });
  };

  // For demo: always show status of connections and onboarding
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-xl">

        <SectionHeading
          title="Connect Your Platforms"
          subtitle="Securely link your business tools in just a few clicks."
          centered={true}
          gradient={true}
        />

        {/* Status badges */}
        <div className="flex gap-4 my-10 justify-center">
          <div className="flex items-center gap-2">
            {googleConnected ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-gray-400" />
            )}
            <span className={googleConnected ? "text-green-600" : "text-gray-500"}>
              Google {googleConnected ? "Connected" : "Not Connected"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {microsoftConnected ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-gray-400" />
            )}
            <span className={microsoftConnected ? "text-green-600" : "text-gray-500"}>
              Microsoft {microsoftConnected ? "Connected" : "Not Connected"}
            </span>
          </div>
        </div>

        {/* Onboarding Form */}
        {showOnboarding && !onboarded ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 transition-shadow">
            <h3 className="text-xl font-semibold mb-2 text-center">Your Details</h3>
            <OnboardingForm
              defaultEmail={session?.user?.email || ""}
              onSubmit={handleOnboardingSubmit}
            />
          </div>
        ) : !onboarded ? (
          <div className="mt-16 grid gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-red-50 rounded-lg">
                    <LogIn className="h-6 w-6 text-red-500" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">Google Workspace</h3>
                  <p className="text-gray-600 mb-6">
                    Connect your Google account to automate Gmail, Calendar, and YouTube tasks.
                  </p>
                  <Button
                    onClick={() => handleOAuthConnect('google')}
                    disabled={isConnecting || !GOOGLE_CLIENT_ID}
                    className="w-full"
                  >
                    Connect Google
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Globe className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">Microsoft 365</h3>
                  <p className="text-gray-600 mb-6">
                    Connect Microsoft to integrate Outlook, Calendar, and 365 automations.
                  </p>
                  <Button
                    onClick={() => handleOAuthConnect('microsoft')}
                    disabled={isConnecting || !MICROSOFT_CLIENT_ID}
                    className="w-full"
                  >
                    Connect Microsoft
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
            <h3 className="text-xl font-semibold mb-2">Onboarding Complete!</h3>
            <p className="text-gray-700">Thank you for connecting your platforms and providing your details.</p>
          </div>
        )}

        {/* Security info */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-gray-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Server className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h4 className="text-lg font-medium mb-2 flex items-center gap-2">
                Enterprise-Grade Security
                <Shield className="h-4 w-4 text-blue-500" />
              </h4>
              <p className="text-gray-600 mb-3">
                Your data is handled securely through OAuth 2.0 authentication.
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Powered by</span>
                <img 
                  src="/lovable-uploads/c8491100-a414-477c-b1aa-293249761c94.png" 
                  alt="AWS Logo" 
                  className="h-6"
                />
              </div>
              <div className="flex items-center gap-1 mt-2">
                <Database className="h-4 w-4 text-blue-400" />
                <span className="text-xs text-gray-500">OAuth 2.0 Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurePlatformConnection;
